// Single entry point for every date display in the app.
//
// Why this exists: `new Date(iso).toLocaleDateString()` (and the
// related `toLocaleString` / `toLocaleTimeString`) defaults to the
// user's local timezone. Records are stored as ISO strings anchored
// at UTC, so a record whose `record_date` is "2025-12-18T00:00:00Z"
// renders as 2025-12-17 for anyone west of London. We saw this as
// "DEC 17" on a timeline card whose stored date was "2025-12-18".
//
// The fix: every Intl.DateTimeFormat call that we own passes
// `timeZone: "UTC"`. Callers go through this module; direct calls
// to `.toLocale*` anywhere else in the app are the bug.
//
// Tradeoff: "time of day" displays also anchor to UTC. A message
// received at 02:00 UTC shows as "02:00" here regardless of the
// reader's timezone. That's the price of never shifting dates. A
// future refactor could split "date shown as UTC, clock shown as
// local" — but the shifting-dates bug is the one that mattered, so
// UTC-all-the-way is the right default.

// "ISO" in this module means YYYY-MM-DD, with or without a time
// suffix. Every formatter tolerates both shapes and returns the
// input string verbatim if parsing fails — we never render
// "Invalid Date" to users.

type DateStyle = "short" | "long" | "iso";

const SHORT_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
};

const LONG_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
  timeZone: "UTC",
};

const TIME_OPTS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "UTC",
  hour12: false,
};

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "UTC",
  hour12: false,
};

// `iso` may be YYYY-MM-DD, a full ISO timestamp, or anything Date()
// can parse. Returns the original string when parsing fails, so
// debug-unfriendly strings still reach the UI instead of the
// literal "Invalid Date".
function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format an ISO date string for display.
 *
 *   formatDate("2026-03-09", "short") → "Mar 9, 2026"
 *   formatDate("2026-03-09", "long")  → "March 9, 2026"
 *   formatDate("2026-03-09", "iso")   → "2026-03-09"
 *   formatDate("2025-12-18T00:00:00Z", "short") → "Dec 18, 2025"
 *
 * Always uses UTC so a midnight-UTC stored date never shifts a day
 * west of its storage value.
 */
export function formatDate(
  iso: string | null | undefined,
  style: DateStyle = "short",
): string {
  if (!iso) return "";
  if (style === "iso") return iso.slice(0, 10);
  const d = toDate(iso);
  if (!d) return iso;
  return d.toLocaleDateString(
    undefined,
    style === "long" ? LONG_OPTS : SHORT_OPTS,
  );
}

/**
 * Format just the time of day (HH:MM, 24h). UTC — see module
 * comment for the tradeoff. Empty string when parsing fails so
 * callers can safely concatenate.
 */
export function formatTime(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return "";
  return d.toLocaleTimeString(undefined, TIME_OPTS);
}

/**
 * Format a full date + time in one string, UTC-anchored.
 *   formatDateTime("2025-12-18T14:30:00Z") → "Dec 18, 2025, 14:30"
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = toDate(iso);
  if (!d) return iso;
  return d.toLocaleString(undefined, DATETIME_OPTS);
}

/**
 * YYYY-MM-DD extraction that reads the UTC calendar day. Handy for
 * grouping / comparison: `formatYmd(a) === formatYmd(b)` is true iff
 * both timestamps fall on the same UTC day, regardless of the
 * reader's locale. Accepts either a Date or an ISO string.
 */
export function formatYmd(input: Date | string | null | undefined): string {
  if (!input) return "";
  const d = input instanceof Date ? input : toDate(input);
  if (!d) return typeof input === "string" ? input.slice(0, 10) : "";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Year-Month heading, e.g. "March 2026". UTC so the string doesn't
 * flip between December and January on New Year's Eve for readers
 * east or west of the storage timezone.
 */
export function formatMonthYear(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

/**
 * True when `a` and `b` fall on the same UTC calendar day. Replaces
 * the earlier pattern of comparing `getFullYear/getMonth/getDate` on
 * Date objects, which used the reader's local timezone and therefore
 * flipped around midnight.
 */
export function sameUtcDay(
  a: string | Date | null | undefined,
  b: string | Date | null | undefined,
): boolean {
  if (!a || !b) return false;
  return formatYmd(a) === formatYmd(b);
}
